import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { VehicleHistoryOccurrence } from '../../../../api/aegVehicleHistory';
import type { AegOccurrence } from '../../../../api/aegOccurrences';
import type { VehicleMaintenanceHistory } from '../../../../api/tas';
import type { BudgetVersionRow, DispatchRow } from '../../../../hooks/useAegVehicleHistory';
import { useAegVehicleHistory } from '../../../../hooks/useAegVehicleHistory';
import { formatDateTimeWithTimezone } from '../../../../utils/dateUtils';
import {
    dispatchStatusLabelIds,
    normalizeOccurrenceDispatchType,
    occurrenceDispatchTypeLabelIds,
    optionsPayer,
    payerLabelIds,
} from '../../../../hooks/useDispatch';
import { formatOccurrenceStepLabel } from '../../../../enums/occurrenceStep';
import { getOccurrenceTypeLabelId } from '../../../../enums/occurrenceType';
import type { IOccurrence } from '../../../support/types';
import { formatAegEnum } from '../../types';
import './styles.css';

const EMPTY = '—';

type VehicleHistoryProps = {
    chassis: string;
};

type SectionProps<T> = {
    title: string;
    columns: string[];
    rows: T[];
    failed: boolean;
    loading: boolean;
    getKey: (row: T, index: number) => string;
    renderRow: (row: T) => ReactNode[];
};

function Section<T>({ title, columns, rows, failed, loading, getKey, renderRow }: SectionProps<T>) {
    const intl = useIntl();
    const t = (id: string, values?: Record<string, number>) =>
        intl.formatMessage({ id: `intl-msg:support.aeg.detail.vehicleHistory.${id}` }, values);

    return (
        <section className='aeg-vehicle-history-section'>
            <h3 className='margin-0 margin-bottom-10 text-bold text-color-darkest'>
                {title}
                {!loading && !failed && rows.length > 0 ? (
                    <small className='margin-left-10 text-normal text-color-dark'>
                        {t('records', { count: rows.length })}
                    </small>
                ) : null}
            </h3>
            <div className='aeg-vehicle-history-table-wrap border rounded'>
                <table className='aeg-vehicle-history-table'>
                    <thead>
                        <tr>
                            {columns.map(column => (
                                <th key={column}>{column}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={getKey(row, index)}>
                                {renderRow(row).map((cell, cellIndex) => (
                                    <td key={columns[cellIndex]}>{cell}</td>
                                ))}
                            </tr>
                        ))}
                        {rows.length === 0 ? (
                            <tr>
                                <td className='text-center text-color-dark' colSpan={columns.length}>
                                    {loading ? t('loading') : failed ? t('error') : t('empty')}
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

const mono = (value?: string | null) => (value ? <span className='aeg-request-detail-mono'>{value}</span> : EMPTY);
const text = (value?: string | number | null) => (value === null || value === undefined || value === '' ? EMPTY : value);

export default function VehicleHistory({ chassis }: VehicleHistoryProps) {
    const intl = useIntl();
    const { loading, failed, aeg, budgets, support, revisions, occurrences, dispatches } =
        useAegVehicleHistory(chassis);

    const t = (id: string) => intl.formatMessage({ id: `intl-msg:support.aeg.detail.vehicleHistory.${id}` });
    const cols = (...keys: string[]) => keys.map(key => t(`col.${key}`));
    const dateTime = (value?: string | null) => (value ? formatDateTimeWithTimezone(value, intl.locale) : EMPTY);

    // `status` do back é um enum interno (OPEN); a situação visível ao usuário é a etapa atual ou a finalização.
    const occurrenceStatus = (row: VehicleHistoryOccurrence) =>
        row.finished
            ? intl.formatMessage({ id: 'intl-msg:tas.occurrence.card.finished' })
            : row.currentStep
              ? formatOccurrenceStepLabel(intl, row.currentStep) || text(row.currentStep)
              : text(row.status);

    const translateIfKnown = (id: string, fallback?: string | null) =>
        intl.messages[id] ? intl.formatMessage({ id }) : text(fallback);

    const dispatchLabel = (labelIds: Record<string, string>, key?: string | null, fallback?: string | null) =>
        key && labelIds[key] ? intl.formatMessage({ id: labelIds[key] }) : text(fallback ?? key);

    const sectionProps = (key: Parameters<typeof failed.includes>[0]) => ({
        loading,
        failed: failed.includes(key),
    });

    // Colunas sem origem no back ficam em branco ('') até serem mapeadas.
    return (
        <div className='display-flex flex-column gap-20'>
            <Section<AegOccurrence>
                {...sectionProps('aeg')}
                columns={cols('chassis', 'partCode', 'causingPart', 'type', 'stops', 'partsQty', 'dealer', 'createdAt', 'winch')}
                getKey={(row, index) => String(row.id ?? index)}
                renderRow={row => [
                    mono(row.chassis),
                    '',
                    '',
                    row.requestType ? formatAegEnum(intl, 'requestType', row.requestType) : EMPTY,
                    '',
                    '',
                    text(row.dealer ?? row.dn),
                    dateTime(row.createdAt),
                    '',
                ]}
                rows={aeg}
                title={t('section.aeg')}
            />
            <Section<BudgetVersionRow>
                {...sectionProps('budgets')}
                columns={cols('profile', 'user', 'budget', 'version', 'versionDate', 'status')}
                getKey={row => `${row.budget.uuid}-${row.versionNumber}`}
                renderRow={row => [
                    text(row.originLabel),
                    text(row.originUserName),
                    <button
                        className='btn btn-link padding-0'
                        disabled
                        key='view'
                        title={t('viewUnavailable')}
                        type='button'
                    >
                        {t('view')}
                    </button>,
                    row.versionNumber.toFixed(2),
                    row.createdAt ? intl.formatDate(row.createdAt) : EMPTY,
                    text(row.statusLabel),
                ]}
                rows={budgets}
                title={t('section.budgets')}
            />
            <Section<IOccurrence>
                {...sectionProps('support')}
                columns={cols('callNumber', 'plateChassis', 'dn', 'dnCode', 'status', 'customer', 'owner', 'createdAt', 'waitingMinutes')}
                getKey={row => String(row.id)}
                renderRow={row => [
                    mono(row.tasOccurrenceNumber),
                    mono(row.licensePlate || row.chassis),
                    text(row.dnDescription),
                    text(row.dn),
                    translateIfKnown(`intl-msg:tas.support.history.status.${row.status}`, row.status),
                    text(row.customer),
                    text(row.createdBy),
                    dateTime(row.createdAt),
                    text(row.timeInStatus),
                ]}
                rows={support}
                title={t('section.support')}
            />
            <Section<VehicleMaintenanceHistory>
                {...sectionProps('revisions')}
                columns={cols('dnCode', 'dealer', 'revisionCode', 'date', 'os', 'km', 'maintenanceGroup')}
                getKey={row => row.id}
                renderRow={row => [
                    text(row.dnCode),
                    text(row.dealer),
                    text(row.revisionCode),
                    row.revisionDate ? intl.formatDate(row.revisionDate, { timeZone: 'UTC' }) : EMPTY,
                    row.serviceOrder != null ? mono(String(row.serviceOrder)) : EMPTY,
                    row.mileage != null ? intl.formatNumber(row.mileage) : EMPTY,
                    text(row.maintenanceGroup),
                ]}
                rows={revisions}
                title={t('section.revisions')}
            />
            <Section<DispatchRow>
                {...sectionProps('occurrences')}
                columns={cols('status', 'occurrence', 'type', 'owner', 'plate', 'sosCityState', 'distance', 'paidBy', 'attended', 'createdBy')}
                getKey={({ dispatch }, index) => dispatch.uuid ?? String(index)}
                renderRow={({ dispatch, occurrence }) => [
                    dispatchLabel(dispatchStatusLabelIds, dispatch.status),
                    mono(occurrence.protocolNumber),
                    dispatchLabel(
                        occurrenceDispatchTypeLabelIds,
                        normalizeOccurrenceDispatchType(dispatch.type ?? undefined),
                        dispatch.type
                    ),
                    text(occurrence.customer),
                    mono(occurrence.vehicle?.licensePlate),
                    text(dispatch.destinationLocation),
                    '',
                    dispatchLabel(
                        payerLabelIds,
                        dispatch.payer,
                        optionsPayer.find(option => option.id === dispatch.payer)?.label ?? dispatch.payer
                    ),
                    '',
                    '',
                ]}
                rows={dispatches}
                title={t('section.dispatches')}
            />
            <Section<VehicleHistoryOccurrence>
                {...sectionProps('occurrences')}
                columns={cols('number', 'origin', 'occurrenceType', 'requestType', 'km', 'createdOn', 'createdBy', 'status')}
                getKey={row => row.uuid}
                renderRow={row => {
                    const typeId = getOccurrenceTypeLabelId(row.occurrenceType);
                    return [
                        mono(row.protocolNumber),
                        text(row.source),
                        typeId ? intl.formatMessage({ id: typeId }) : text(row.occurrenceType),
                        text(row.subject),
                        row.vehicle?.odometer != null ? intl.formatNumber(row.vehicle.odometer) : EMPTY,
                        dateTime(row.createdAt),
                        text(row.createdBy),
                        occurrenceStatus(row),
                    ];
                }}
                rows={occurrences}
                title={t('section.occurrences')}
            />
        </div>
    );
}
